/* eslint-disable no-unused-vars */
/********************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2016, 2022
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} **/

import React, { useEffect, useState } from 'react';
import { MultiSelect, Select, SelectItem, TextInput, TextArea, Checkbox, FileUploaderButton, Button } from 'carbon-components-react';
import PropTypes from 'prop-types';
import generator from '../utils/generator';
import { withTranslation } from 'react-i18next';
import { getTranslatedExtensions } from '../utils/translate';
import classNames from 'classnames';
import { cleanUpKey } from '../utils/common';
import { get } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useApi } from '../contexts/ApiHandler';

const LANG_EXTENSION = 'x-ibm-languages';
const formatMap = {
  date: 'date',
  'date-time': 'date-time',
  double: 'number',
  email: 'email',
  float: 'number',
  int32: 'number',
  int64: 'number',
  number: 'number',
  password: 'password',
  url: 'url',
  file: 'file'
};

export function TestToolParameter({
  name,
  parameter,
  parameterValues,
  pathName,
  method,
  contentTypeHeader,
  onChange,
  isBody,
  maxDepth,
  apiReferences,
  exampleArrayItems
}) {
  const [body, setBody] = useState(false);
  const [isFileUpload, setIsFileUpload] = useState(false);
  const [canGenerate, setCanGenerate] = useState(true);
  const [description, setDescription] = useState(null);
  const [required, setRequired] = useState(false);
  const [isEnum, setIsEnum] = useState(false);
  const [isArray, setIsArray] = useState(false);
  const [isBoolean, setIsBoolean] = useState(false);
  const [selectedExample, setSelectedExample] = useState(-1);
  const [arrayCount, setArrayCount] = useState(0);
  const [value, setValue] = useState(null);
  const { api, apiHandler } = useApi();
  const { t } = useTranslation();

  useEffect(() => {
    setRequired(parameter.required === true);

    let description;
    if (parameter?.description !== 'undefined') {
      if (parameter[LANG_EXTENSION]) {
        description = {};
        description[LANG_EXTENSION] = parameter[LANG_EXTENSION];
      }
    }
    description = getTranslatedExtensions(description, 'description') || parameter.description || '';
    setDescription(description);

    const isArray = parameter?.type === 'array' && parameter?.items;
    const isBoolean = parameter?.type === 'boolean' || parameter?.schema?.type === 'boolean' || parameter?.items?.type === 'boolean';
    const isFileUpload =
      parameter?.type === 'file' ||
      (parameter.type === 'string' && (parameter.format === 'binary' || parameter.format === 'base64')) ||
      parameter?.schema?.type === 'file' ||
      (parameter.schema?.type === 'string' && (parameter.schema?.format === 'binary' || parameter.schema?.format === 'base64')) ||
      (parameter.type === 'array' &&
        (parameter.items?.type === 'file' ||
          (parameter.items?.type === 'string' && (parameter.items?.format === 'binary' || parameter.items?.format === 'base64'))));
    const isEnum = parameter?.enum || parameter?.items?.enum || parameter?.schema?.enum;

    setBody(parameter?.in === 'body' || isBody);
    setIsEnum(isEnum);
    setIsArray(isArray);
    setIsBoolean(isBoolean);
    setIsFileUpload(isFileUpload);

    if (
      parameter.format === 'JSON' || // generator doesn't know this type...
      parameter.format === 'date' || // HTML5 takes care of this for us
      parameter.type === 'boolean' || // generating booleans isn't very valuable...
      parameter.name === 'X-Client-Certificate' || // makes no sense to generate a simple string in place of a PEM certificate
      isFileUpload || // generating files doesn't make any sense
      isEnum
    ) {
      setCanGenerate(false);
    }

    if (isEnum && !isArray && parameter.value === undefined) {
      parameter.value = get(parameter, 'enum[0]') ?? get(parameter, 'schema.enum[0]') ?? undefined;
    }
  }, [api, apiHandler, isBody, parameter]);

  let val = parameterValues[name]?.value;
  useEffect(() => {
    // todo deal with value = false || parameterValues[name].type === 'boolean'
    let value = '';
    if (parameterValues[name]?.value && (parameterValues[name]?.value || parameterValues[name]?.type === 'boolean')) {
      value = parameterValues[name].value;
    }
    setValue(value);
  }, [name, parameterValues, val]);

  useEffect(() => {}, [arrayCount]);

  if (parameter?.$ref) {
    parameter = apiHandler.getLocalReference(parameter.$ref, api);
  }

  let enumMulti;
  if (isEnum && isArray) {
    enumMulti = [];
    parameter.items.enum.map((value) => {
      return enumMulti.push({ id: cleanUpKey(value), text: value });
    });
  }

  const allowEmptyValueChange = (e) => {
    let isChecked = (e || e.checked || (e.target && e.target.checked)) === true;
    if (isChecked) {
      onValueChange({
        target: {
          allowEmptyValue: true,
          value: ''
        }
      });
    } else {
      onValueChange({
        target: {
          allowEmptyValue: false,
          value: undefined
        }
      });
    }
  };

  const getType = () => {
    if (formatMap[parameter.format]) {
      return formatMap[parameter.format];
    } else if (parameter.type === 'number') {
      return 'number';
    } else {
      return 'text';
    }
  };

  const getStep = () => {
    if (parameter.format === 'float' || parameter.format === 'double') {
      return 'any';
    }

    if (parameter.format === 'int32' || parameter.format === 'int64') {
      return 1;
    }

    return null;
  };

  const generateExample = async () => {
    if (parameter.collectionFormat) {
      parameter.collectionFormat = null;
    }
    let example = await generator.generateExampleParameter({
      api,
      pathName: pathName,
      verb: method,
      parameter: { ...parameter, testTool: true },
      contentTypeHeader: contentTypeHeader,
      skipBeautify: false,
      maxDepth: maxDepth,
      references: apiReferences, // TODO: This doesn't exist anywhere? Not passed in, etc.
      numItems: exampleArrayItems
    });
    if (get(example, 'example') && example.hasMultipleExamples) {
      let newSelectedExample = selectedExample;
      newSelectedExample++;
      if (newSelectedExample >= Object.keys(example.example).length) {
        newSelectedExample = 0;
      }
      example = example.example[Object.keys(example.example)[newSelectedExample]];
      setSelectedExample(newSelectedExample);
    } else if (get(example, 'example')) {
      example = example.example;
    }
    // Ensure we update the parent (mock the event object)
    onValueChange({
      target: {
        value: example
      }
    });
  };

  const onFileUpload = (evt, index) => {
    let newValue = [];
    let uploadValue = {};
    if (value) {
      newValue = value;
    }
    if (evt.target && evt.target.files && evt.target.files[0]) {
      uploadValue.file = evt.target.files[0];
      var reader = new FileReader();
      reader.onload = (read) => {
        uploadValue.fileContents = read.target.result;
        newValue[index] = uploadValue;
        newValue = Object.values(newValue);
        updateValue(newValue);
      };
      reader.readAsDataURL(uploadValue.file);
    } else if (evt.target && evt.target.allowEmptyValue === true) {
      newValue = '';
    } else if (evt.target && evt.target.allowEmptyValue === false) {
      newValue = undefined;
    }
    if (newValue && newValue !== '' && index) {
      newValue[index] = uploadValue;
      newValue = Object.values(newValue);
    }
    updateValue(newValue);
  };

  const onValueChange = (evt) => {
    let value;
    if (evt.target && evt.target.value) {
      value = evt.target.value;
    } else if (evt.selectedItems) {
      value = evt.selectedItems.map((a) => a.text);
    } else if (typeof evt === 'boolean') {
      value = evt;
    } else if (evt.target && evt.target.allowEmptyValue === true) {
      value = '';
    } else if (evt.target && evt.target.allowEmptyValue === false) {
      value = undefined;
    }
    updateValue(value);
  };

  const updateValue = (value) => {
    // Notify parent of change in value
    if (onChange && typeof onChange === 'function') {
      onChange(name, value);
    }
  };

  // in openapi3, body parameters don't have a name so define a generic one here
  if (api?.openapi && body) {
    name = 'body';
  }

  let addArrayComponent = () => {
    let newValue = !value ? [null] : Object.values(value).concat([null]);
    updateValue(newValue);
  };
  const removeArrayComponent = (index, value) => {
    delete value[index];
    updateValue(value);
  };

  return (
    <div className={classNames('parameterOuter', `parameter-${cleanUpKey(parameter.type)}-${cleanUpKey(name)}`)}>
      {!body && !isEnum && !isFileUpload && !isBoolean && (
        <div className="parameterOther">
          <div className="parameterHeader">
            <div>
              <label htmlFor={name} className="parameterName">
                {name}
              </label>
              {required && <span className="requiredHighlight">*</span>}
            </div>

            {canGenerate && (
              <a className="generateButton" role="button" tabIndex="0" onKeyDown={generateExample} onClick={generateExample}>
                {t('assembly_generate_data')}
              </a>
            )}
          </div>
          <div className="clearBoth"></div>

          {parameter.name === 'X-Client-Certificate' && (
            <TextArea
              id={name}
              rows={4}
              cols={50}
              labelText={name}
              hideLabel={true}
              required={required}
              placeholder={description}
              name={name}
              onChange={onValueChange}
            />
          )}

          {parameter.name !== 'X-Client-Certificate' && (
            <TextInput
              id={name}
              onChange={onValueChange}
              labelText={name}
              hideLabel={true}
              required={required}
              placeholder={description}
              name={name}
              type={getType()}
              min={typeof parameter.minimum === 'undefined' ? null : parameter.minimum}
              max={typeof parameter.maximum === 'undefined' ? null : parameter.maximum}
              minLength={typeof parameter.minLength === 'undefined' ? null : parameter.minLength}
              maxLength={typeof parameter.maxLength === 'undefined' ? null : parameter.maxLength}
              step={getStep()}
              pattern={typeof parameter.pattern === 'undefined' ? null : parameter.pattern}
              value={value}
            />
          )}
        </div>
      )}

      {isFileUpload && (
        <div className="parameterOther">
          <label htmlFor={name} className="parameterName">
            {name}
          </label>

          {required && <span className="requiredHighlight">*</span>}
          {parameter.type !== 'array' ? (
            <FileUploaderButton labelText={t('choose_file')} onChange={onFileUpload} />
          ) : (
            <>
              {value !== '' &&
                value?.map((element, i) => (
                  <>
                    <br />
                    <FileUploaderButton
                      id={i}
                      labelText={element?.file?.name ?? t('choose_file')}
                      onChange={(e) => {
                        onFileUpload(e, i);
                      }}
                    />
                    <Button onClick={() => removeArrayComponent(i, value)}>Remove</Button>
                    <br />
                  </>
                ))}
              <br />
              <Button onClick={() => addArrayComponent(arrayCount)}>Add</Button>
            </>
          )}
        </div>
      )}

      {isEnum &&
        (!enumMulti ? (
          <div className="parameterEnum">
            <label htmlFor={name} className="parameterName">
              {name}
            </label>
            {required && <span className="requiredHighlight">*</span>}
            <Select id={name} hideLabel className="bx--select__input" required={required} name={name} labelText={name} onChange={onValueChange} value={value}>
              {required && (value === '' || value === null) && <SelectItem value="" text="" />}
              {!required && <SelectItem value="__omit" text="---" />}
              {parameter.enum &&
                parameter.enum.map(function (value, i) {
                  return <SelectItem key={i} value={value} text={value} />;
                })}
              {parameter.schema &&
                parameter.schema.enum &&
                parameter.schema.enum.map(function (value, i) {
                  return <SelectItem key={i} value={value} text={value} />;
                })}
            </Select>
          </div>
        ) : (
          <div className="parameterEnum">
            <label htmlFor={name} className="parameterName">
              {name}
            </label>
            {required && <span className="requiredHighlight">*</span>}
            <MultiSelect
              id={name}
              useTitleInItem={false}
              label={description}
              items={enumMulti}
              required={required}
              name={name}
              itemToString={(item) => (item ? item.text : '')}
              onChange={onValueChange}
              value={value}
            />
          </div>
        ))}

      {body && (
        <div className="parameterBody">
          <div className="parameterHeader">
            <div>
              <label htmlFor={name} className="parameterName">
                {name}
              </label>
              {required && <span className="requiredHighlight">*</span>}
            </div>

            {canGenerate && (
              <a className="generateButton" role="button" tabIndex="0" onKeyDown={generateExample} onClick={generateExample}>
                {t('assembly_generate_data')}
              </a>
            )}
          </div>
          <div className="clearBoth"></div>
          <TextArea
            id={name}
            rows={4}
            cols={50}
            labelText={t('generated')}
            required={required}
            placeholder={description}
            name={name}
            value={value}
            onChange={onValueChange}
          />
        </div>
      )}

      {isBoolean &&
        (required ? (
          <div className="parameterBoolean requiredBoolean">
            <label htmlFor={name} className="parameterName">
              {name}
            </label>
            <span className="requiredHighlight">*</span>
            <Checkbox id={name} labelText="" onChange={onValueChange} checked={value ? value : false} />
          </div>
        ) : (
          <div className="parameterBoolean notRequiredBoolean bx--select">
            <label htmlFor={name} className="parameterName">
              {name}
            </label>

            <div className="clearBoth"></div>

            <Select id={name} hideLabel onChange={onValueChange}>
              <SelectItem value="__omit" text="---" />
              <SelectItem value="true" text="true" />
              <SelectItem value="false" text="false" />
            </Select>
          </div>
        ))}

      {parameter.in === 'query' && parameter.allowEmptyValue && (
        <Checkbox
          id={parameter.name + 'SendEmpty'}
          onChange={(e) => allowEmptyValueChange(e)}
          labelText="Send empty value"
          disabled={value && value !== '__omit'}
        />
      )}
    </div>
  );
}

TestToolParameter.propTypes = {
  parameter: PropTypes.object,
  name: PropTypes.string,
  parameterValues: PropTypes.object,
  openApi: PropTypes.object,
  pathName: PropTypes.string,
  method: PropTypes.string,
  contentTypeHeader: PropTypes.string,
  onValueChange: PropTypes.func,
  isBody: PropTypes.bool
};

export default withTranslation()(TestToolParameter);
